'use client'

import * as React from 'react'
import type { Language } from 'codegen/types'
import { Link } from '@repo/design-system-cf/components/link'
import { Typography } from '@repo/web-ui/components/typography'
import { useLocale, useTranslations } from 'next-intl'
import { tw } from 'utils/classnames'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@repo/web-ui/components/breadcrumb'
import { addOrReplaceLanguageInUrl } from 'cf-i18n/helpers'
import { AppRoutes } from '@repo/web-utils/app-routes'

type MyAccountPageVariant =
  | 'subscriptions'
  | 'subscription-details'
  | 'subscription-manage'
  | 'cancel-subscription'
  | 'orders-and-statements'
  | 'order-details'
  | 'my-classes'
  | 'notification-settings'
  | 'downloads-and-keys'
  | 'account-activity'
  | 'social-logins'
  | 'billing-details'
  | 'shipping-address'
  | 'payment-methods'
  | 'edit-my-profile'
  | 'edit-email-and-password'
  | 'affiliate-statistics'
  | 'referral-url-generator'
  | 'referrals'
  | 'affiliate-visits'
  | 'affiliate-settings'
  | 'affiliate-dashboard'
  | 'affiliate-bannerd-and-creatives'
  | 'delete-account'
  | 'manage-subscription'
  | 'manage-subscription-empty'

type MyAccountPageVariantExtended = MyAccountPageVariant

export function MyAccountPageLayout({
  className = '',
  variant,
  children,
  useEmptyPlaceholder,
}: {
  className?: string
  variant: MyAccountPageVariantExtended
  children?: React.ReactNode
  useEmptyPlaceholder?: boolean
}) {
  const locale = useLocale() as Language
  const t = useTranslations('pages.my_account')
  const {
    title = '',
    description = '',
    breadcrumbs,
    emptyState,
  }: {
    title?: string
    description?: string
    breadcrumbs?: { title: string; path?: string }[]
    emptyState?: React.ReactNode
  } = {
    subscriptions: () => ({
      title: t('pages.subscriptions.title'),
      breadcrumbs: [
        {
          title: t('pages.my_account.title'),
          path: addOrReplaceLanguageInUrl(AppRoutes.MY_ACCOUNT, locale),
        },
        { title: t('pages.subscriptions.title') },
      ],
      emptyState: (
        <p className="pb-4">
          {t.rich('pages.subscriptions.empty', {
            link: content => (
              <Link
                href={addOrReplaceLanguageInUrl(AppRoutes.SUBSCRIPTION, locale)}
                variant={'primary-green'}
              >
                {content}
              </Link>
            ),
          })}
        </p>
      ),
    }),
    'subscription-details': () => ({
      title: t('pages.subscription_details.title'),
      breadcrumbs: [
        {
          title: t('pages.my_account.title'),
          path: addOrReplaceLanguageInUrl(AppRoutes.MY_ACCOUNT, locale),
        },
        { title: t('pages.subscriptions.title') },
      ],
    }),
    'subscription-manage': () => ({
      breadcrumbs: [
        {
          title: t('pages.my_account.title'),
          path: addOrReplaceLanguageInUrl(AppRoutes.MY_ACCOUNT, locale),
        },
        {
          title: t('pages.subscriptions.title'),
          path: addOrReplaceLanguageInUrl(AppRoutes.MY_ACCOUNT_SUBSCRIPTIONS, locale),
        },
        { title: t('pages.manage_subscription.title') },
      ],
    }),
    'cancel-subscription': () => ({
      breadcrumbs: [
        {
          title: t('pages.my_account.title'),
          path: addOrReplaceLanguageInUrl(AppRoutes.MY_ACCOUNT, locale),
        },
        {
          title: t('pages.subscriptions.title'),
          path: addOrReplaceLanguageInUrl(AppRoutes.MY_ACCOUNT_SUBSCRIPTIONS, locale),
        },
        { title: t('pages.cancel_subscription.breadcrumb_title') },
      ],
    }),
    'orders-and-statements': () => ({
      title: t('pages.orders.title'),
      breadcrumbs: [
        {
          title: t('pages.my_account.title'),
          path: addOrReplaceLanguageInUrl(AppRoutes.MY_ACCOUNT, locale),
        },
        { title: t('pages.orders.title') },
      ],
      emptyState: (
        <p className="pb-4">
          {t.rich('pages.orders.empty', {
            link: content => (
              <Link
                href={addOrReplaceLanguageInUrl(AppRoutes.SEARCH, locale)}
                variant={'primary-green'}
              >
                {content}
              </Link>
            ),
          })}
        </p>
      ),
    }),
    'order-details': () => ({
      title: t('pages.orders.title'),
      breadcrumbs: [
        {
          title: t('pages.my_account.title'),
          path: addOrReplaceLanguageInUrl(AppRoutes.MY_ACCOUNT, locale),
        },
        { title: t('pages.orders.title') },
      ],
      emptyState: (
        <p className="pb-4">
          {t.rich('pages.orders.empty', {
            link: content => (
              <Link
                href={addOrReplaceLanguageInUrl(AppRoutes.SEARCH, locale)}
                variant={'primary-green'}
              >
                {content}
              </Link>
            ),
          })}
        </p>
      ),
    }),
    'my-classes': () => ({
      title: t('pages.my_classes.title'),
      breadcrumbs: [
        {
          title: t('pages.my_account.title'),
          path: addOrReplaceLanguageInUrl(AppRoutes.MY_ACCOUNT, locale),
        },
        { title: t('pages.my_classes.title') },
      ],
    }),
    'notification-settings': () => ({
      title: t('pages.email_settings.title'),
      breadcrumbs: [
        {
          title: t('pages.my_account.title'),
          path: addOrReplaceLanguageInUrl(AppRoutes.MY_ACCOUNT, locale),
        },
        { title: t('pages.email_settings.title') },
      ],
    }),
    'downloads-and-keys': () => ({
      title: t('pages.downloads_and_keys.title'),
      description: t('pages.downloads_and_keys.description'),
      breadcrumbs: [
        {
          title: t('pages.my_account.title'),
          path: addOrReplaceLanguageInUrl(AppRoutes.MY_ACCOUNT, locale),
        },
        { title: t('pages.downloads_and_keys.title') },
      ],
    }),
    'account-activity': () => ({
      title: t('pages.account_activity.title'),
      breadcrumbs: [
        {
          title: t('pages.my_account.title'),
          path: addOrReplaceLanguageInUrl(AppRoutes.MY_ACCOUNT, locale),
        },
        { title: t('pages.account_activity.title') },
      ],
    }),
    'social-logins': () => ({
      title: t('pages.social_logins.title'),
      breadcrumbs: [
        {
          title: t('pages.my_account.title'),
          path: addOrReplaceLanguageInUrl(AppRoutes.MY_ACCOUNT, locale),
        },
        { title: t('pages.social_logins.title') },
      ],
    }),
    'billing-details': () => ({
      title: t('pages.billing_details.title'),
      breadcrumbs: [
        {
          title: t('pages.my_account.title'),
          path: addOrReplaceLanguageInUrl(AppRoutes.MY_ACCOUNT, locale),
        },
        { title: t('pages.billing_details.title') },
      ],
    }),
    'shipping-address': () => ({
      title: t('pages.shipping_address.title'),
      description: t('pages.shipping_address.description'),
      breadcrumbs: [
        {
          title: t('pages.my_account.title'),
          path: addOrReplaceLanguageInUrl(AppRoutes.MY_ACCOUNT, locale),
        },
        { title: t('pages.shipping_address.title') },
      ],
    }),
    'payment-methods': () => ({
      title: t('pages.payment_methods.title'),
      breadcrumbs: [
        {
          title: t('pages.my_account.title'),
          path: addOrReplaceLanguageInUrl(AppRoutes.MY_ACCOUNT, locale),
        },
        { title: t('pages.payment_methods.title') },
      ],
    }),
    'edit-my-profile': () => ({
      title: t('pages.edit_my_profile.title'),
      description: t('pages.edit_my_profile.description'),
      breadcrumbs: [
        {
          title: t('pages.my_account.title'),
          path: addOrReplaceLanguageInUrl(AppRoutes.MY_ACCOUNT, locale),
        },
        { title: t('pages.edit_my_profile.title') },
      ],
    }),
    'edit-email-and-password': () => ({
      title: t('pages.edit_email_and_password.title'),
      breadcrumbs: [
        {
          title: t('pages.my_account.title'),
          path: addOrReplaceLanguageInUrl(AppRoutes.MY_ACCOUNT, locale),
        },
        { title: t('pages.edit_email_and_password.title') },
      ],
    }),
    'affiliate-statistics': () => ({
      title: t('pages.affiliate_statistics.title'),
      description: t('pages.affiliate_statistics.description'),
      breadcrumbs: [
        {
          title: t('pages.my_account.title'),
          path: addOrReplaceLanguageInUrl(AppRoutes.MY_ACCOUNT, locale),
        },
        { title: t('pages.affiliate_statistics.title') },
      ],
    }),
    'referral-url-generator': () => ({
      title: t('pages.referral_url_generator.title'),
      description: t('pages.referral_url_generator.description'),
      breadcrumbs: [
        {
          title: t('pages.my_account.title'),
          path: addOrReplaceLanguageInUrl(AppRoutes.MY_ACCOUNT, locale),
        },
        { title: t('pages.referral_url_generator.title') },
      ],
    }),
    referrals: () => ({
      title: t('pages.referrals.title'),
      description: t('pages.referrals.description'),
      breadcrumbs: [
        {
          title: t('pages.my_account.title'),
          path: addOrReplaceLanguageInUrl(AppRoutes.MY_ACCOUNT, locale),
        },
        { title: t('pages.referrals.title') },
      ],
    }),
    'affiliate-visits': () => ({
      title: t('pages.affiliate_visits.title'),
      description: t('pages.affiliate_visits.description'),
      breadcrumbs: [
        {
          title: t('pages.my_account.title'),
          path: addOrReplaceLanguageInUrl(AppRoutes.MY_ACCOUNT, locale),
        },
        { title: t('pages.affiliate_visits.title') },
      ],
    }),
    'affiliate-settings': () => ({
      title: t('pages.affiliate_settings.title'),
      description: t('pages.affiliate_settings.description'),
      breadcrumbs: [
        {
          title: t('pages.my_account.title'),
          path: addOrReplaceLanguageInUrl(AppRoutes.MY_ACCOUNT, locale),
        },
        { title: t('pages.affiliate_settings.title') },
      ],
    }),
    'affiliate-dashboard': () => ({
      title: t('pages.affiliate_dashboard.title'),
      description: t('pages.affiliate_dashboard.description'),
      breadcrumbs: [
        {
          title: t('pages.my_account.title'),
          path: addOrReplaceLanguageInUrl(AppRoutes.MY_ACCOUNT, locale),
        },
        { title: t('pages.affiliate_dashboard.title') },
      ],
    }),
    'affiliate-bannerd-and-creatives': () => ({
      title: t('pages.affiliate_bannerd_and_creatives.title'),
      description: t('pages.affiliate_bannerd_and_creatives.description') || '',
      breadcrumbs: [
        {
          title: t('pages.my_account.title'),
          path: addOrReplaceLanguageInUrl(AppRoutes.MY_ACCOUNT, locale),
        },
        { title: t('pages.affiliate_bannerd_and_creatives.title') },
      ],
    }),
    'delete-account': () => ({
      breadcrumbs: [
        {
          title: t('pages.my_account.title'),
          path: addOrReplaceLanguageInUrl(AppRoutes.MY_ACCOUNT, locale),
        },
        { title: t('pages.edit_email_and_password.title') },
      ],
    }),
    'manage-subscription': () => ({
      title: t('pages.manage_subscription.title'),
      breadcrumbs: [
        {
          title: t('pages.my_account.title'),
          path: addOrReplaceLanguageInUrl(AppRoutes.MY_ACCOUNT, locale),
        },
        { title: t('pages.manage_subscription.title') },
      ],
    }),
    'manage-subscription-empty': () => ({
      breadcrumbs: [
        {
          title: t('pages.my_account.title'),
          path: addOrReplaceLanguageInUrl(AppRoutes.MY_ACCOUNT, locale),
        },
        { title: t('pages.manage_subscription.title') },
      ],
    }),
  }[variant]()

  return (
    <div
      className={tw(
        'bg-cf-white @container flex flex-col gap-8 rounded-[1.5rem] @lg:px-6 py-6 px-4',
        className,
      )}
    >
      {breadcrumbs ? (
        <div>
          <Breadcrumb>
            <BreadcrumbList className="flex-wrap">
              {breadcrumbs.map(({ title, path }, index) => (
                <React.Fragment key={title}>
                  {path ? (
                    <Link key={title} href={path}>
                      {title}
                    </Link>
                  ) : (
                    <BreadcrumbItem className="whitespace-normal [word-break:break-word]">
                      <BreadcrumbPage>{title}</BreadcrumbPage>
                    </BreadcrumbItem>
                  )}
                  {index < breadcrumbs.length - 1 ? (
                    <BreadcrumbSeparator className="text-primary-500 text-14 inline px-1.5">
                      /
                    </BreadcrumbSeparator>
                  ) : null}
                </React.Fragment>
              ))}
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      ) : null}
      {title || description ? (
        <div className="flex flex-col gap-3">
          {title ? (
            <h1 className="text-cf-black whitespace-normal text-[1.75rem] font-bold leading-[2.5rem] [word-break:break-word]">
              {title}
            </h1>
          ) : null}
          {description ? <Typography variant="body">{description}</Typography> : null}
        </div>
      ) : null}
      <div className="flex flex-col">{useEmptyPlaceholder ? emptyState : children}</div>
    </div>
  )
}
